﻿import { useEffect, useRef, useState } from 'react';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Ref } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import { api } from '../lib/api.js';
import {
  categorizeStripePaymentError,
  captureTrackingFromUrl,
  getPlausibleAttribution,
  getTracking,
  installPlausible,
  shouldTrackSalesPageScroll,
  trackCheckoutContactCompleted,
  trackCheckoutViewed,
  trackCheckoutStarted,
  trackCtaClick,
  trackPaymentCanceled,
  trackPaymentFailed,
  trackPaymentInitiated,
  trackPaymentMethodSelected,
  trackScrollProgress,
  type CtaPosition,
  type PaymentMethod,
} from '../lib/tracking.js';
import { getStripe } from '../lib/stripeClient.js';
import {
  CHECKOUT_SECTION_ID,
  buildPaymentReturnUrl,
  cleanPaymentReturnUrl,
  getPaymentReturnOrderId,
  handleCheckoutCtaClick,
  isTerminalCheckoutStatus,
  removeCheckoutFragment,
  scrollToCheckoutSection,
  shouldResetCheckoutAttemptForApiError,
  shouldShowStickyCheckoutCta,
} from '../domain/checkout.js';
import {
  ADMIN_RESEND_CONFIRMATION,
  ADMIN_RESEND_ERROR,
  ADMIN_RESEND_SUCCESS,
  buildAdminResendEndpoint,
  canShowAdminResendAction,
  formatAdminDownloadUsage,
} from '../domain/admin.js';
import { salesPageContent } from './salesPageContent.js';
import ebookCover from '../assets/optimized/cover-1054.webp';
import ebookCoverSmall from '../assets/optimized/cover-360.webp';
import ebookCoverMedium from '../assets/optimized/cover-720.webp';
import brandLogo from '../assets/optimized/logo-64.webp';
import brandLogoRetina from '../assets/optimized/logo-128.webp';
import previewExplanation from '../assets/optimized/evidence-675.webp';
import previewExplanationSmall from '../assets/optimized/evidence-384.webp';
import previewSummary from '../assets/optimized/preview-tools-675.webp';
import previewSummarySmall from '../assets/optimized/preview-tools-384.webp';
import previewExercise from '../assets/optimized/preview-exercise-675.webp';
import previewExerciseSmall from '../assets/optimized/preview-exercise-384.webp';

type PaymentInit = {
  orderId: string;
  clientSecret: string;
  amount: number;
  currency: string;
};

type PayPalConfig = {
  paypal: {
    clientId: string;
    environment: 'sandbox' | 'live';
  };
};

type PayPalCreateResult = {
  orderId: string;
};

const PAYPAL_FRONTEND_ENABLED = true;

type OrderStatus = {
  orderId: string;
  status: 'requires_payment' | 'pending' | 'processing' | 'paid' | 'failed' | 'canceled' | 'refunded';
  emailDeliveryStatus: string | null;
  amount: number;
  currency: string;
};

type AdminOrder = {
  id: string;
  email: string;
  status: string;
  amount: number;
  currency: string;
  email_delivery_status: string | null;
  created_at: string | null;
  download_access?: Array<{
    download_count: number;
    max_downloads: number | null;
    revoked_at: string | null;
  }> | null;
};

const ebookPreviewImages = [previewExplanation, previewSummary, previewExercise] as const;
const ebookPreviewSmallImages = [previewExplanationSmall, previewSummarySmall, previewExerciseSmall] as const;
const coverSrcSet = `${ebookCoverSmall} 360w, ${ebookCoverMedium} 720w, ${ebookCover} 1054w`;
const checkoutAmount = 990;
const checkoutCurrency = 'eur';

export function App() {
  const path = window.location.pathname;
  const paymentReturnOrderId = getPaymentReturnOrderId(window.location);

  useEffect(() => {
    removeCheckoutFragment();
    installPlausible();
    captureTrackingFromUrl();
  }, [path]);

  if (paymentReturnOrderId) return <PaymentReturnPage orderId={paymentReturnOrderId} />;
  if (path.startsWith('/checkout')) return <CheckoutRedirect />;
  if (path.startsWith('/merci')) return <SuccessPage />;
  if (path.startsWith('/telechargement/')) return <DownloadTokenPage token={decodeURIComponent(path.split('/').pop() ?? '')} />;
  if (path.startsWith('/admin')) return <AdminPage />;
  return <LandingPage />;
}

function LandingPage() {
  const productIntroRef = useRef<HTMLElement | null>(null);
  const finalOfferRef = useRef<HTMLElement | null>(null);
  const [hasPassedProductIntro, setHasPassedProductIntro] = useState(false);
  const [isFinalOfferVisible, setIsFinalOfferVisible] = useState(false);
  const [isCheckoutEngaged, setIsCheckoutEngaged] = useState(false);
  const showStickyCta = shouldShowStickyCheckoutCta({ hasPassedProductIntro, isFinalOfferVisible, isCheckoutEngaged });

  useEffect(() => {
    if (!shouldTrackSalesPageScroll(window.location.pathname)) return;

    let frame = 0;
    const updateScrollDepth = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight,
        );
        trackScrollProgress({
          scrollY: window.scrollY,
          documentHeight,
          viewportHeight: window.innerHeight,
        });
      });
    };

    updateScrollDepth();
    window.addEventListener('scroll', updateScrollDepth, { passive: true });
    window.addEventListener('resize', updateScrollDepth);
    window.addEventListener('load', updateScrollDepth);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', updateScrollDepth);
      window.removeEventListener('resize', updateScrollDepth);
      window.removeEventListener('load', updateScrollDepth);
    };
  }, []);

  useEffect(() => {
    const productIntro = productIntroRef.current;
    const finalOffer = finalOfferRef.current;
    if (!productIntro || !finalOffer) return;

    let frame = 0;
    const updateStickyState = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const productBottom = productIntro.getBoundingClientRect().bottom;
        const offerTop = finalOffer.getBoundingClientRect().top;
        const checkoutTop = document.getElementById(CHECKOUT_SECTION_ID)?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
        setHasPassedProductIntro(productBottom < 0);
        setIsFinalOfferVisible(offerTop < window.innerHeight * 0.9 || checkoutTop < window.innerHeight * 0.95);
      });
    };

    updateStickyState();
    window.addEventListener('scroll', updateStickyState, { passive: true });
    window.addEventListener('resize', updateStickyState);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', updateStickyState);
      window.removeEventListener('resize', updateStickyState);
    };
  }, []);

  const scrollToCheckout = (ctaPosition: CtaPosition) => {
    handleCheckoutCtaClick({
      track: () => trackCtaClick(ctaPosition),
    });
  };

  return (
    <main className="landing">
      <header className="letter-header" aria-label="En-tête">
        <a className="brand" href="/" aria-label="Quotidien TDAH">
          <img src={brandLogo} srcSet={`${brandLogo} 64w, ${brandLogoRetina} 128w`} sizes="40px" width="128" height="128" decoding="async" alt="" aria-hidden="true" />
          <span>{salesPageContent.brand}</span>
        </a>
      </header>

      <article className="sales-letter">
        <header className="letter-hero">
          <h1>
            Environ <span>4 adultes sur 5</span> concernés par le TDAH ne le savent pas encore.
          </h1>
          <p className="lead"><HighlightedText text={salesPageContent.hero.body} /></p>
        </header>

        <div className="letter-body">
          <section className="letter-block">
            <h2>{salesPageContent.opening.title}</h2>
            <Paragraphs items={salesPageContent.opening.paragraphs} />
            <blockquote className="central-question">{salesPageContent.opening.question}</blockquote>
          </section>

          <ProductSpotlight ref={productIntroRef} />

          <section className="letter-block">
            <Paragraphs items={salesPageContent.recognition.paragraphs} />
          </section>

          <section className="letter-block">
            <h2>{salesPageContent.guide.title}</h2>
            <Paragraphs items={salesPageContent.guide.paragraphs} />
            <BeforeAfter />
          </section>

          <section className="letter-block">
            <h2>{salesPageContent.benefits.title}</h2>
            <BulletList items={salesPageContent.benefits.items} />
            <Paragraphs items={salesPageContent.benefits.closing} />
          </section>

          <section className="letter-block">
            <h2>{salesPageContent.reading.title}</h2>
            <p className="content-placeholder">{salesPageContent.reading.countPlaceholder}</p>
            <Paragraphs items={salesPageContent.reading.paragraphs} />
            <PreviewPlaceholders />
          </section>

          <section className="letter-block">
            <h2>{salesPageContent.freeInfo.title}</h2>
            <Paragraphs items={salesPageContent.freeInfo.paragraphs} />
          </section>

          <section className="letter-block">
            <h2>{salesPageContent.fit.title}</h2>
            <Paragraphs items={salesPageContent.fit.paragraphs} />
            <blockquote className="letter-quote">{salesPageContent.fit.quote}</blockquote>
          </section>
        </div>
      </article>

      <section className="final-offer" ref={finalOfferRef} aria-label="Offre ebook">
        <div className="checkout-offer">
          <figure className="checkout-cover">
            <img src={ebookCoverSmall} srcSet={coverSrcSet} sizes="(min-width: 768px) 140px, 132px" width="1054" height="1492" loading="lazy" decoding="async" alt="Couverture de l’ebook Je me reconnais dans le TDAH… et maintenant ?" />
          </figure>
          <div>
            <p className="eyebrow">{salesPageContent.offer.eyebrow}</p>
            <h2>{salesPageContent.offer.title}</h2>
            <p className="offer-subtitle">{salesPageContent.offer.subtitle}</p>
            <ul className="offer-facts">
              {salesPageContent.offer.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
            <div className="price-block compact" aria-label="Prix de lancement">
              <span className="old-price">Prix habituel : <del>{salesPageContent.oldPrice}</del></span>
              <span className="launch-price">{salesPageContent.launchPrice}</span>
            </div>
            <p className="payment-line">{salesPageContent.offer.paymentLine}</p>
          </div>
        </div>
      </section>

      <CheckoutSection onCheckoutEngaged={() => setIsCheckoutEngaged(true)} />

      <StickyCheckoutCta visible={showStickyCta} onClick={() => scrollToCheckout('sticky')} />

      <footer className="letter-footer">
        <nav aria-label="Liens légaux">
          {salesPageContent.footer.links.map((link) => (
            <a key={link.label} href={link.href}>{link.label}</a>
          ))}
        </nav>
      </footer>
    </main>
  );
}

function Paragraphs({ items }: { items: string[] }) {
  return (
    <>
      {items.map((paragraph) => (
        <p key={paragraph}><HighlightedText text={paragraph} /></p>
      ))}
    </>
  );
}

function HighlightedText({ text }: { text: string }) {
  const highlightedPhrases = [
    'faire bonne figure',
    'culpabiliser',
    'les listes que tu recommences',
    'la pression de dernière minute',
    'une tâche de cinq minutes',
    'une précision troublante',
    'un point de départ',
    'réellement important',
    'un chemin clair',
    'une étiquette attribuée par un PDF',
    'de meilleures questions',
    'leur dispersion',
    'un parcours cohérent',
    'pas une nouvelle liste de symptômes',
    'un chemin structuré',
    'ne remplacera jamais un professionnel',
    'mieux comprendre ce que tu vis',
  ];
  const parts: Array<{ text: string; highlighted: boolean }> = [{ text, highlighted: false }];

  for (const phrase of highlightedPhrases) {
    for (let index = 0; index < parts.length; index += 1) {
      const part = parts[index];
      if (!part || part.highlighted || !part.text.includes(phrase)) continue;
      const [before, afterStart] = part.text.split(phrase, 2);
      const after = part.text.slice(before.length + phrase.length);
      parts.splice(
        index,
        1,
        ...(before ? [{ text: before, highlighted: false }] : []),
        { text: phrase, highlighted: true },
        ...(after || afterStart === '' ? [{ text: after, highlighted: false }] : []),
      );
    }
  }

  return (
    <>
      {parts.map((part, index) =>
        part.highlighted ? (
          <strong className="text-highlight" key={`${part.text}-${index}`}>{part.text}</strong>
        ) : (
          <span key={`${part.text}-${index}`}>{part.text}</span>
        ),
      )}
    </>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="letter-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function BeforeAfter() {
  return (
    <div className="before-after" aria-label="Avant après">
      <blockquote>
        <span>Avant</span>
        {salesPageContent.guide.before}
      </blockquote>
      <blockquote>
        <span>Après</span>
        {salesPageContent.guide.after}
      </blockquote>
    </div>
  );
}

function PreviewPlaceholders() {
  return (
    <div className="preview-placeholders" aria-label="Aperçus attendus de l’ebook">
      {salesPageContent.reading.previews.map((preview, index) => (
        <figure className="preview-placeholder" key={preview.title}>
          <div className="preview-image-frame">
            <img src={ebookPreviewImages[index]} srcSet={`${ebookPreviewSmallImages[index]} 384w, ${ebookPreviewImages[index]} 675w`} sizes="(min-width: 768px) 638px, (min-width: 632px) 598px, calc(100vw - 34px)" width="675" height="955" loading="lazy" decoding="async" alt={preview.alt} />
          </div>
          <figcaption>{preview.title}</figcaption>
        </figure>
      ))}
    </div>
  );
}

function ProductSpotlight({ ref }: { ref: Ref<HTMLElement> }) {
  return (
    <section className="product-spotlight" aria-label="Présentation de l’ebook" ref={ref}>
      <figure className="spotlight-cover">
        <img src={ebookCoverSmall} srcSet={coverSrcSet} sizes="(min-width: 451px) 360px, calc((100vw - 32px) * 0.86)" width="1054" height="1492" loading="lazy" decoding="async" alt="Couverture de l’ebook Je me reconnais dans le TDAH… et maintenant ?" />
      </figure>
    </section>
  );
}

function StickyCheckoutCta({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  return (
    <div className={`sticky-cta-wrap${visible ? ' is-visible' : ''}`} aria-hidden={!visible}>
      <button type="button" className="sticky-cta" onClick={onClick} tabIndex={visible ? 0 : -1}>
        <span>{salesPageContent.stickyCta.label}</span>
        <small>{salesPageContent.stickyCta.price}</small>
      </button>
    </div>
  );
}

function CheckoutRedirect() {
  useEffect(() => {
    window.history.replaceState(null, '', '/');
    scrollToCheckoutSection();
  }, []);

  return <LandingPage />;
}

function CheckoutSection({ onCheckoutEngaged }: { onCheckoutEngaged?: () => void }) {
  const checkoutSectionRef = useRef<HTMLElement | null>(null);
  const [email, setEmail] = useState(() => sessionStorage.getItem('checkout_email') ?? '');
  const [firstName, setFirstName] = useState(() => sessionStorage.getItem('checkout_name') ?? '');
  const [paypalConfig, setPayPalConfig] = useState<PayPalConfig | null>(null);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [stripeLoadError, setStripeLoadError] = useState(false);
  const [stripeAttempt, setStripeAttempt] = useState(0);
  const [paypalLoadError, setPayPalLoadError] = useState(false);
  const [paypalAttempt, setPayPalAttempt] = useState(0);
  const [paymentWidgetsReady, setPaymentWidgetsReady] = useState(false);
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedName = firstName.trim();
  const contactValid = normalizedName.length >= 2 && isValidEmail(normalizedEmail);
  const contactMessage = contactValid ? null : 'Renseigne ton prénom et ton email pour lancer le paiement.';
  const checkoutStartedRef = useRef(false);
  const paymentMethodSelectedRef = useRef(false);

  useEffect(() => {
    const section = checkoutSectionRef.current;
    if (!section || typeof IntersectionObserver !== 'function') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.2)) {
          trackCheckoutViewed();
          observer.disconnect();
        }
      },
      { threshold: [0.2] },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const section = checkoutSectionRef.current;
    if (!section || typeof IntersectionObserver !== 'function') {
      setPaymentWidgetsReady(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setPaymentWidgetsReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: '800px 0px' },
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!paymentWidgetsReady) return;
    let active = true;
    setStripeLoadError(false);
    getStripe().then(
      (instance) => { if (active) setStripe(instance); },
      () => { if (active) setStripeLoadError(true); },
    );
    return () => { active = false; };
  }, [paymentWidgetsReady, stripeAttempt]);

  useEffect(() => {
    if (!PAYPAL_FRONTEND_ENABLED || !paymentWidgetsReady) return;
    let active = true;
    setPayPalLoadError(false);
    api<PayPalConfig>('/api/payments/config').then(
      (config) => { if (active) setPayPalConfig(config); },
      () => { if (active) setPayPalLoadError(true); },
    );
    return () => { active = false; };
  }, [paymentWidgetsReady, paypalAttempt]);

  useEffect(() => {
    if (!contactValid) return;
    trackCheckoutContactCompleted();
  }, [contactValid, normalizedEmail, normalizedName]);

  function markCheckoutStarted() {
    if (checkoutStartedRef.current) return;
    checkoutStartedRef.current = true;
    onCheckoutEngaged?.();
    trackCheckoutStarted();
  }

  function markPaymentMethodSelected(method: PaymentMethod) {
    if (paymentMethodSelectedRef.current) return;
    paymentMethodSelectedRef.current = true;
    trackPaymentMethodSelected(method);
  }

  return (
    <section className="sales-section checkout-section" id={CHECKOUT_SECTION_ID} ref={checkoutSectionRef}>
      <section
        className="panel checkout-panel"
        aria-label="Paiement sécurisé"
      >
        <h2>Paiement sécurisé</h2>
        <p className="muted">Livraison immédiate par email</p>
        <div className="price">
          <span>Total</span>
          <strong>{formatAmount(checkoutAmount, checkoutCurrency)}</strong>
        </div>
        <div className="stack">
          <label>
            Prénom
            <input
              autoComplete="given-name"
              required
              value={firstName}
              onFocus={markCheckoutStarted}
              onChange={(event) => {
                markCheckoutStarted();
                setFirstName(event.target.value);
              }}
            />
          </label>
          <label>
            Email de livraison
            <input
              inputMode="email"
              autoComplete="email"
              required
              value={email}
              onFocus={markCheckoutStarted}
              onChange={(event) => {
                markCheckoutStarted();
                setEmail(event.target.value);
              }}
            />
          </label>

          <div className="checkout-separator" role="separator" aria-label="Paiement express">
            <span>Paiement express</span>
          </div>
          {PAYPAL_FRONTEND_ENABLED && paymentWidgetsReady && (
            <div className="payment-method-block" aria-label="Paiements rapides">
              {paypalLoadError && (
                <p className="error" role="alert">
                  PayPal n’a pas pu charger. <button type="button" className="button ghost" onClick={() => setPayPalAttempt((attempt) => attempt + 1)}>Réessayer PayPal</button>
                </p>
              )}
              <PayPalButton
                config={paypalConfig}
                contactValid={contactValid}
                contactMessage={contactMessage}
                checkoutInput={{ email: normalizedEmail, firstName: normalizedName }}
                onCheckoutStarted={markCheckoutStarted}
                onPaymentMethodSelected={markPaymentMethodSelected}
              />
            </div>
          )}
        </div>

        {stripe ? (
          <Elements
            key={stripeAttempt}
            stripe={stripe}
            options={{
              mode: 'payment',
              amount: checkoutAmount,
              currency: checkoutCurrency,
              appearance: { theme: 'stripe' },
            }}
          >
            <PaymentForm
              contactValid={contactValid}
              contactMessage={contactMessage}
              checkoutInput={{ name: normalizedName, email: normalizedEmail }}
              onCheckoutEngaged={onCheckoutEngaged}
              onCheckoutStarted={markCheckoutStarted}
              onPaymentMethodSelected={markPaymentMethodSelected}
              onRetry={() => setStripeAttempt((attempt) => attempt + 1)}
            />
          </Elements>
        ) : stripeLoadError ? (
          <div role="alert">
            <p className="error">Le paiement par carte n’a pas pu charger. Vérifie ta connexion puis réessaie.</p>
            <button type="button" className="button ghost" onClick={() => setStripeAttempt((attempt) => attempt + 1)}>Réessayer le paiement par carte</button>
          </div>
        ) : (
          <div className="payment-widgets-loading" aria-live="polite">Chargement sécurisé des moyens de paiement…</div>
        )}
      </section>
      <p className="checkout-note">
        Support : <a href={`mailto:${salesPageContent.supportEmail}`}>{salesPageContent.supportEmail}</a>
      </p>
      <p className="checkout-disclaimer">{salesPageContent.footer.disclaimer}</p>
    </section>
  );
}

function PaymentForm({
  contactValid,
  contactMessage,
  checkoutInput,
  onCheckoutEngaged,
  onCheckoutStarted,
  onPaymentMethodSelected,
  onRetry,
}: {
  contactValid: boolean;
  contactMessage: string | null;
  checkoutInput: { name: string; email: string };
  onCheckoutEngaged?: () => void;
  onCheckoutStarted: () => void;
  onPaymentMethodSelected: (method: PaymentMethod) => void;
  onRetry: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elementReady, setElementReady] = useState(false);
  const [elementLoadError, setElementLoadError] = useState(false);
  const submittingRef = useRef(false);
  const expressMethodRef = useRef<PaymentMethod>('card');
  const [submittedOrderId, setSubmittedOrderId] = useState<string | null>(null);

  async function confirm(method: PaymentMethod) {
    if (!stripe || !elements || submittingRef.current) return;
    if (!contactValid) {
      setError(contactMessage);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    onCheckoutEngaged?.();

    try {
      const submitResult = await elements.submit();
      if (submitResult.error) {
        setError(submitResult.error.message ?? 'Vérifie tes informations de carte.');
        trackPaymentFailed(method, categorizeStripePaymentError(submitResult.error));
        return;
      }
      const payment = await createStripePayment({ retryAfterExpiredAttempt: true });
      await api('/api/payments/stripe/method', {
        method: 'POST',
        body: JSON.stringify({ orderId: payment.orderId, method }),
      });
      trackPaymentInitiated(method);
      const result = await stripe.confirmPayment({
        elements,
        clientSecret: payment.clientSecret,
        confirmParams: {
          return_url: buildPaymentReturnUrl(window.location.origin, payment.orderId),
          payment_method_data: {
            billing_details: {
              name: checkoutInput.name,
              email: checkoutInput.email,
            },
          },
        },
        redirect: 'if_required',
      });

      if (result.error) {
        setError(result.error.message ?? 'Le paiement a echoue.');
        trackPaymentFailed(method, categorizeStripePaymentError(result.error));
        return;
      }

      clearCheckoutAttempt();
      sessionStorage.setItem('current_order_id', payment.orderId);
      setSubmittedOrderId(payment.orderId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de préparer le paiement.');
      trackPaymentFailed(method, 'unknown');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    onCheckoutStarted();
    onPaymentMethodSelected('card');
    await confirm('card');
  }

  if (submittedOrderId) return <OrderConfirmationPanel orderId={submittedOrderId} />;

  return (
    <form className="stack" onSubmit={submit}>
      <ExpressCheckoutElement
        onClick={(event) => {
          const clickEvent = event as { expressPaymentType?: string; resolve?: (options?: unknown) => void };
          const method = normalizeExpressMethod(clickEvent.expressPaymentType);
          expressMethodRef.current = method;
          onCheckoutStarted();
          onPaymentMethodSelected(method);
          if (!contactValid) {
            setError(contactMessage);
            return;
          }
          clickEvent.resolve?.({});
        }}
        onConfirm={() => confirm(expressMethodRef.current)}
        options={{ buttonTheme: { applePay: 'black', googlePay: 'black' }, paymentMethods: { link: 'never', paypal: 'never' } }}
      />
      <div className="checkout-separator" role="separator" aria-label="Ou payer par carte">
        <span>Ou payer par carte</span>
      </div>
      <PaymentElement
        options={{ layout: 'tabs', defaultValues: { billingDetails: { name: checkoutInput.name, email: checkoutInput.email } } }}
        onReady={() => { setElementReady(true); setElementLoadError(false); }}
        onLoadError={() => { setElementReady(false); setElementLoadError(true); }}
        onFocus={() => {
          onCheckoutStarted();
          onPaymentMethodSelected('card');
        }}
      />
      {elementLoadError && (
        <div role="alert">
          <p className="error">Le formulaire de carte n’a pas pu charger.</p>
          <button type="button" className="button ghost" onClick={onRetry}>Recharger le formulaire</button>
        </div>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      <button className="button primary" disabled={!stripe || !elementReady || submitting || !contactValid}>
        {submitting ? 'Paiement en cours...' : salesPageContent.offer.cta}
      </button>
    </form>
  );

  async function createStripePayment(options: { retryAfterExpiredAttempt: boolean }): Promise<PaymentInit> {
    const checkoutAttemptId = getCheckoutAttemptId();
    sessionStorage.setItem('checkout_email', checkoutInput.email);
    sessionStorage.setItem('checkout_name', checkoutInput.name);
    try {
      const result = await api<PaymentInit>('/api/payments/create', {
        method: 'POST',
        body: JSON.stringify({
          email: checkoutInput.email,
          firstName: checkoutInput.name,
          checkoutAttemptId,
          tracking: getTracking({ source: detectSocialSource() }),
          attribution: getPlausibleAttribution(),
        }),
      });
      sessionStorage.setItem('current_order_id', result.orderId);
      return result;
    } catch (err) {
      const code = typeof err === 'object' && err && 'code' in err ? (err as { code?: unknown }).code : undefined;
      if (options.retryAfterExpiredAttempt && shouldResetCheckoutAttemptForApiError(code)) {
        clearCheckoutAttempt();
        return createStripePayment({ retryAfterExpiredAttempt: false });
      }
      throw err;
    }
  }
}

function PayPalButton({
  config,
  contactValid,
  contactMessage,
  checkoutInput,
  onCheckoutStarted,
  onPaymentMethodSelected,
}: {
  config: PayPalConfig | null;
  contactValid: boolean;
  contactMessage: string | null;
  checkoutInput: { email: string; firstName: string };
  onCheckoutStarted: () => void;
  onPaymentMethodSelected: (method: PaymentMethod) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<PayPalButtonElement | null>(null);
  const renderedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [submittedOrderId, setSubmittedOrderId] = useState<string | null>(null);
  const latestInputRef = useRef({ contactValid, contactMessage, checkoutInput });

  useEffect(() => {
    latestInputRef.current = { contactValid, contactMessage, checkoutInput };
  }, [contactValid, contactMessage, checkoutInput.email, checkoutInput.firstName]);

  useEffect(() => {
    if (!config || !containerRef.current || renderedRef.current) return;
    renderedRef.current = true;
    loadPayPalSdk(config.paypal.environment)
      .then(async (paypal) => {
        const sdkInstance = await paypal.createInstance({
          clientId: config.paypal.clientId,
          components: ['paypal-payments'],
          pageType: 'checkout',
          locale: 'fr-BE',
        });
        const methods = await sdkInstance.findEligibleMethods({ currencyCode: 'EUR' });
        if (!methods.isEligible('paypal')) {
          setError('PayPal est indisponible pour ce paiement.');
          return;
        }

        const paymentSession = sdkInstance.createPayPalOneTimePaymentSession({
          onApprove: async ({ orderId }) => {
            setCapturing(true);
            try {
              const result = await api<{ orderId: string; status: string }>('/api/payments/paypal/capture', {
                method: 'POST',
                body: JSON.stringify({ paypalOrderId: orderId }),
              });
              clearCheckoutAttempt();
              sessionStorage.setItem('current_order_id', result.orderId);
              setSubmittedOrderId(result.orderId);
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Paiement PayPal impossible.');
              trackPaymentFailed('paypal', 'unknown', 'modal');
            } finally {
              setCapturing(false);
            }
          },
          onCancel: () => {
            setCapturing(false);
            trackPaymentCanceled('paypal');
          },
          onError: () => {
            setCapturing(false);
            setError('Paiement PayPal impossible.');
            trackPaymentFailed('paypal', 'unknown', 'modal');
          },
        });

        const paypalButton = document.createElement('paypal-button') as PayPalButtonElement;
        paypalButton.type = 'pay';
        paypalButton.className = !latestInputRef.current.contactValid ? 'paypal-disabled' : '';
        paypalButton.addEventListener('click', async () => {
          const latest = latestInputRef.current;
          onCheckoutStarted();
          onPaymentMethodSelected('paypal');
          if (!latest.contactValid) {
            setError(latest.contactMessage);
            return;
          }
          setError(null);
          setCapturing(true);
          try {
            await paymentSession.start({ presentationMode: 'modal' }, createPayPalOrder(latest.checkoutInput));
          } catch {
            setError('PayPal a échoué. Réessaie ou paie par carte.');
            trackPaymentFailed('paypal', 'unknown', 'modal');
          } finally {
            setCapturing(false);
          }
        });
        containerRef.current!.append(paypalButton);
        buttonRef.current = paypalButton;
      })
      .catch(() => setError('PayPal est momentanément indisponible.'));
  }, [config]);

  useEffect(() => {
    if (!buttonRef.current) return;
    buttonRef.current.className = !contactValid ? 'paypal-disabled' : '';
  }, [contactValid]);

  async function createPayPalOrder(input: { email: string; firstName: string }): Promise<PayPalCreateResult> {
    trackPaymentInitiated('paypal');
    return api<PayPalCreateResult>('/api/payments/paypal/create', {
      method: 'POST',
      body: JSON.stringify({
        email: input.email,
        firstName: input.firstName,
        checkoutAttemptId: getCheckoutAttemptId(),
        tracking: getTracking({ source: detectSocialSource() }),
        attribution: getPlausibleAttribution(),
      }),
    });
  }

  if (submittedOrderId) return <OrderConfirmationPanel orderId={submittedOrderId} />;

  return (
    <div className="paypal-block">
      <div ref={containerRef} className={!contactValid ? 'paypal-disabled' : ''} />
      {capturing && <p className="muted">Confirmation PayPal...</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function SuccessPage() {
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('order') ?? sessionStorage.getItem('current_order_id');

  return <OrderConfirmationPage orderId={orderId} />;
}

function PaymentReturnPage({ orderId }: { orderId: string }) {
  useEffect(() => {
    sessionStorage.setItem('current_order_id', orderId);
    cleanPaymentReturnUrl(window.history);
  }, [orderId]);

  return <OrderConfirmationPage orderId={orderId} />;
}

function OrderConfirmationPage({ orderId }: { orderId: string | null }) {
  return (
    <main className="page">
      <OrderConfirmationPanel orderId={orderId} />
    </main>
  );
}

function OrderConfirmationPanel({ orderId }: { orderId: string | null }) {
  const [status, setStatus] = useState<OrderStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    let count = 0;
    let cancelled = false;

    async function check() {
      count += 1;
      try {
        const result = await api<OrderStatus>(`/api/orders/status?orderId=${orderId}`);
        if (cancelled) return;
        setStatus(result);
        if (isTerminalCheckoutStatus(result.status)) clearCheckoutAttempt();
        if (result.status === 'paid') return;
        if (count < 8 && ['pending', 'processing', 'requires_payment'].includes(result.status)) {
          window.setTimeout(check, 2500);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Commande introuvable.');
      }
    }

    check();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return (
    <section className="panel">
      <h1>Merci</h1>
      {!orderId && <p className="error">Reference de commande manquante.</p>}
      {error && <p className="error">{error}</p>}
      {status?.status === 'paid' ? (
        <>
          <p className="success">Paiement confirme.</p>
          <p className="muted">Ton lien de telechargement est envoye par email. Si tu ne le vois pas, verifie les courriers indesirables ou demande un renvoi depuis la page de telechargement.</p>
        </>
      ) : status?.status === 'failed' || status?.status === 'canceled' ? (
        <p className="error">Le paiement n'a pas ete confirme.</p>
      ) : status?.status === 'refunded' ? (
        <p className="error">Cette commande a ete remboursee. L'acces au telechargement est revoke.</p>
      ) : (
        <p className="muted">Nous confirmons ton paiement. Cette page se met a jour automatiquement pendant quelques secondes.</p>
      )}
    </section>
  );
}

function DownloadTokenPage({ token }: { token: string }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const apiUrl = `/api/download/${encodeURIComponent(token)}`;

  return (
    <main className="page">
      <section className="panel">
        <h1>Telechargement</h1>
        <p className="muted">Le lien direct du PDF est temporaire. Il sera genere au moment du clic.</p>
        <a className="button primary" href={apiUrl}>
          Telecharger l'ebook
        </a>
        <form
          className="stack secondary-form"
          onSubmit={async (event) => {
            event.preventDefault();
            await api('/api/download/resend', { method: 'POST', body: JSON.stringify({ email }) });
            setMessage('Si une commande payee existe pour cette adresse, un nouveau lien sera envoye.');
          }}
        >
          <label>
            Lien expire ? Demander un renvoi
            <input inputMode="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
          </label>
          <button className="button ghost">Recevoir un nouveau lien</button>
        </form>
        {message && <p className="success">{message}</p>}
      </section>
    </main>
  );
}

function AdminPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [resendingOrderId, setResendingOrderId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  async function login(event: React.FormEvent) {
    event.preventDefault();
    const { supabase } = await import('../lib/supabaseClient.js');
    if (!supabase) {
      setError('Configuration Supabase publique manquante.');
      return;
    }
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      setError(authError.message);
      return;
    }
    setToken(data.session?.access_token ?? null);
  }

  async function loadOrders() {
    const data = await api<{ orders: AdminOrder[] }>('/api/admin/orders', {
      headers: { authorization: `Bearer ${token}` },
    });
    setOrders(data.orders);
  }

  async function resendEbook(orderId: string) {
    if (!token || resendingOrderId) return;
    const confirmed = window.confirm(ADMIN_RESEND_CONFIRMATION);
    if (!confirmed) return;

    setResendingOrderId(orderId);
    setActionMessage(null);
    setError(null);
    try {
      await api(buildAdminResendEndpoint(orderId), {
        method: 'POST',
        headers: { authorization: `Bearer ${token}` },
      });
      setActionMessage(ADMIN_RESEND_SUCCESS);
      await loadOrders();
    } catch {
      setError(ADMIN_RESEND_ERROR);
    } finally {
      setResendingOrderId(null);
    }
  }

  useEffect(() => {
    if (token) void loadOrders();
  }, [token]);

  if (!token) {
    return (
      <main className="page">
        <section className="panel">
          <h1>Admin</h1>
          <form className="stack" onSubmit={login}>
            <label>Email<input value={email} onChange={(event) => setEmail(event.target.value)} /></label>
            <label>Mot de passe<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
            {error && <p className="error">{error}</p>}
            <button className="button primary">Connexion</button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="page admin">
      <section className="panel wide">
        <h1>Commandes</h1>
        <button className="button ghost" onClick={loadOrders}>Actualiser</button>
        {actionMessage && <p className="success">{actionMessage}</p>}
        {error && <p className="error">{error}</p>}
        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Email</th><th>Statut</th><th>Montant</th><th>Email</th><th>Téléchargements</th><th>Date</th><th>Action</th></tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                return (
                  <tr key={order.id}>
                    <td>{order.email}</td>
                    <td>{order.status}</td>
                    <td>{formatAmount(order.amount, order.currency)}</td>
                    <td>{order.email_delivery_status}</td>
                    <td>{formatAdminDownloadUsage(order.download_access)}</td>
                    <td>{order.created_at ? new Date(order.created_at).toLocaleString('fr-FR') : ''}</td>
                    <td>
                      {canShowAdminResendAction(order.status) ? (
                        <button
                          type="button"
                          className="button ghost"
                          disabled={resendingOrderId === order.id}
                          onClick={() => void resendEbook(order.id)}
                        >
                          {resendingOrderId === order.id ? 'Envoi...' : 'Renvoyer l’ebook'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function getCheckoutAttemptId() {
  const existing = sessionStorage.getItem('checkout_attempt_id');
  if (existing) return existing;
  const next = crypto.randomUUID();
  sessionStorage.setItem('checkout_attempt_id', next);
  return next;
}

function clearCheckoutAttempt() {
  sessionStorage.removeItem('checkout_attempt_id');
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function normalizeExpressMethod(value: string | undefined): PaymentMethod {
  if (value === 'apple_pay') return 'apple_pay';
  if (value === 'google_pay') return 'google_pay';
  if (value === 'link') return 'link';
  return 'card';
}

function loadPayPalSdk(environment: 'sandbox' | 'live'): Promise<PayPalNamespace> {
  if (window.paypal) return Promise.resolve(window.paypal);
  const existing = document.querySelector<HTMLScriptElement>('script[data-paypal-sdk="true"]');
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => (window.paypal ? resolve(window.paypal) : reject(new Error('PayPal SDK missing'))));
      existing.addEventListener('error', () => reject(new Error('PayPal SDK failed')));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = environment === 'sandbox' ? 'https://www.sandbox.paypal.com/web-sdk/v6/core' : 'https://www.paypal.com/web-sdk/v6/core';
    script.async = true;
    script.dataset.paypalSdk = 'true';
    script.onload = () => (window.paypal ? resolve(window.paypal) : reject(new Error('PayPal SDK missing')));
    script.onerror = () => reject(new Error('PayPal SDK failed'));
    document.head.appendChild(script);
  });
}

function formatAmount(amount: number, currency: string) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
}

function detectSocialSource() {
  const referrer = document.referrer.toLowerCase();
  if (referrer.includes('tiktok')) return 'tiktok';
  if (referrer.includes('instagram')) return 'instagram';
  return 'direct';
}

type PayPalNamespace = {
  createInstance: (options: {
    clientId: string;
    components: ['paypal-payments'];
    pageType: 'checkout';
    locale: 'fr-BE';
  }) => Promise<PayPalSdkInstance>;
};

type PayPalSdkInstance = {
  findEligibleMethods: (options: { currencyCode: 'EUR' }) => Promise<{
    isEligible: (method: 'paypal') => boolean;
  }>;
  createPayPalOneTimePaymentSession: (options: {
    onApprove: (data: { orderId: string }) => Promise<void> | void;
    onCancel: () => void;
    onError: (error: unknown) => void;
  }) => PayPalPaymentSession;
};

type PayPalPaymentSession = {
  start: (options: { presentationMode: 'modal' }, orderPromise: Promise<{ orderId: string }>) => Promise<void>;
};

type PayPalButtonElement = HTMLElement & {
  type: 'pay';
};

declare global {
  interface Window {
    paypal?: PayPalNamespace;
  }
}


